import { Badge, Box, Center, Divider, Flex, IconButton, Spinner, Tooltip, VStack } from "@chakra-ui/react";
import { useParams } from "react-router";

import { SIGNATURES_PENDING_DESCRIPTION } from "@equidefi/constants/agreements";
import { DownloadIconButton } from "@equidefi/portals/components/investments/DownloadButton";
import { useInvestmentSignatures } from "@equidefi/portals/hooks/useAgreements";
import { BannerAlert, Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { VAULT_ROUTES } from "@/constants/routes";
import { useInvestment } from "@/hooks/useInvestments";
import { VaultContainer } from "../../components";
import { InvestmentTitleCard, VaultPageHeader } from "../components";

const SignerRow = ({ signer }) => {
  const isSigned = signer.status === "signed";

  return (
    <Flex gap="3" align="center" w="full" py={2}>
      <Box flex={1} minW={0}>
        <Text fontWeight="600" mb={0}>
          {signer.full_name || signer.name}
        </Text>
        {signer.email && (
          <Text textStyle="body2" color="gray.600" m={0}>
            {signer.email}
          </Text>
        )}
      </Box>
      <Badge colorScheme={isSigned ? "green" : "orange"} variant="solid" borderRadius="md" px={2} py={1} fontSize="xs">
        {isSigned ? "SIGNED" : "PENDING"}
      </Badge>
    </Flex>
  );
};

const DocumentRow = ({ label, disabled, children, ...rest }) => (
  <Flex
    align="center"
    w="full"
    py={3}
    borderBottom="1px"
    borderColor="gray.200"
    _last={{ border: "unset", pb: 0 }}
    {...rest}>
    <Text m="0" mr="auto" color={disabled ? "gray.400" : undefined}>
      {label}
    </Text>
    {children}
  </Flex>
);

const SignerStatusBanner = ({ investors }) => {
  if (investors.length === 0) return null;

  const isSigned = investors.every((party) => party.status === "signed");

  if (isSigned) {
    return (
      <BannerAlert
        status="success"
        title="Agreement Signed"
        description="The subscription agreement has been signed."
      />
    );
  }

  return <BannerAlert status="warning" title="Signatures Pending" description={SIGNATURES_PENDING_DESCRIPTION} />;
};

const CountersignBanner = ({ canDownload }) => {
  if (canDownload) {
    return (
      <BannerAlert
        status="success"
        title="Agreement Countersigned"
        description="Your subscription agreement is now available for download."
      />
    );
  }

  return (
    <BannerAlert
      status="info"
      title="Agreement Not Countersigned"
      description="You will be able to download the subscription agreement once it has been countersigned by the issuer."
    />
  );
};

const AgreementsPage = () => {
  const { id } = useParams();
  const { data: investment, isFetchedAfterMount } = useInvestment(id, {
    refetchOnMount: true,
  });

  const { data: signatures, isFetched: isSignaturesFetched } = useInvestmentSignatures(investment?.id);

  if (!isFetchedAfterMount || !isSignaturesFetched) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  const investors = signatures?.investors?.filter((party) => party.vault.is_signer) ?? [];
  const agreement = signatures?.investment_agreement;
  const hasAgreement = investors.length > 0;

  const canDownloadAgreement = !!signatures?.is_countersigned && !!investment?.offering_tranche_id;
  const offeringDocuments = investment?.offering?.documents ?? [];

  const issuer = investment?.issuer;
  const offering = investment?.offering;

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(investment?.id) },
          { label: "Agreements & Downloads" },
        ]}
        backTo={VAULT_ROUTES.investmentDetails(investment?.id)}
        backLabel="Back To Investment"
      />

      <VStack spacing="4" align="stretch">
        <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />
        <Box bg="white" borderRadius="md" boxShadow="md" p="4">
          <Heading fontWeight="bold" as="h1" mb="1">
            Agreement Signers
          </Heading>
          <Text textStyle="body2" mb="4">
            The subscription agreement must be signed by all signers.
          </Text>

          {investors.length === 0 ? (
            <>
              <Divider mb={4} />
              <Text color="gray.500" m={0}>
                No agreements have been generated yet.
              </Text>
            </>
          ) : (
            <>
              <SignerStatusBanner investors={investors} />
              <Box mt={4}>
                {investors.map((investor) => (
                  <SignerRow key={investor.id} signer={investor} />
                ))}
              </Box>
            </>
          )}
        </Box>

        <Box bg="white" borderRadius="md" boxShadow="md" p="4">
          <Heading fontWeight="bold" as="h1" mb="3">
            Subscription Documents
          </Heading>

          {hasAgreement && (
            <>
              <CountersignBanner canDownload={canDownloadAgreement} />
              <DocumentRow label="Subscription Agreement" disabled={!canDownloadAgreement} mt={4}>
                <Tooltip
                  label="Available once the agreement is countersigned by the issuer."
                  isDisabled={canDownloadAgreement}
                  hasArrow
                  shouldWrapChildren>
                  <DownloadIconButton
                    investmentId={investment?.id}
                    investmentAgreementId={agreement?.id}
                    isDisabled={!canDownloadAgreement || !agreement?.id}
                    aria-label="Download Subscription Agreement"
                  />
                </Tooltip>
              </DocumentRow>
            </>
          )}
          {/* Investor Package */}
          {offeringDocuments.map((document) => (
            <DocumentRow key={document.id} label={document.name}>
              <IconButton
                as="a"
                color="equidefi.blue"
                bg="none"
                icon={<Icon.Download size="1.5em" />}
                href={document.url}
                download
                rel="noreferrer"
                aria-label={`Download ${document.name}`}
              />
            </DocumentRow>
          ))}
        </Box>
      </VStack>
    </VaultContainer>
  );
};

export default AgreementsPage;
